import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Header } from '@/components/Header';
import { OwnershipFilter } from '@/components/OwnershipFilter';
import { VisitCard } from '@/features/visits/VisitCard';
import { Skeleton } from '@/components/ui/skeleton';
import { visitsApi } from '@/api/visits';
import type { OwnershipFilterValue, WeeklyVisitItem } from '@/api/visits';
import type { SortedVisitItem } from '@/features/visits/hooks/useSortedVisits';
import { useClientIndicators } from '@/features/visits/hooks/useClientIndicators';

const OWNERSHIP_FILTER_KEY = 'visits-ownership-filter';

type FilterValue = OwnershipFilterValue[] | 'all';

function loadOwnershipFilter(): FilterValue {
  try {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return ['own'];
}

export function DepartedClientsPage() {
  const navigate = useNavigate();
  const [ownershipFilters, setOwnershipFilters] = useState<FilterValue>(loadOwnershipFilter);

  const handleOwnershipChange = useCallback((value: FilterValue) => {
    setOwnershipFilters(value);
    localStorage.setItem(OWNERSHIP_FILTER_KEY, JSON.stringify(value));
  }, []);

  const filterParam = ownershipFilters === 'all' ? 'all' : ownershipFilters;

  const { data, isLoading } = useQuery({
    queryKey: ['departed-clients', filterParam],
    queryFn: () => visitsApi.getDepartedClients(filterParam),
  });

  const sortedItems: SortedVisitItem[] = useMemo(() => {
    if (!data?.visits) return [];
    return data.visits.map((v: WeeklyVisitItem): SortedVisitItem => ({
      ...v,
      priority: 'regular',
    }));
  }, [data]);

  const clientIds = useMemo(() => sortedItems.map(v => v.client.id), [sortedItems]);
  const { data: indicatorsData } = useClientIndicators(clientIds);

  const handleItemClick = useCallback((item: SortedVisitItem) => {
    navigate(`/visits/client/${item.visitTargetId}`, {
      state: { clientId: item.client.id },
    });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Ушедшие клиенты"
        backTo="/more"
        rightContent={
          <OwnershipFilter value={ownershipFilters} onChange={handleOwnershipChange} />
        }
      />

      <main className="p-4 space-y-2">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))
        ) : sortedItems.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">
            Нет ушедших клиентов
          </div>
        ) : (
          <>
            <div className="text-sm text-muted-foreground mb-2">
              {sortedItems.length} {sortedItems.length === 1 ? 'клиент' : sortedItems.length < 5 ? 'клиента' : 'клиентов'}
            </div>
            {sortedItems.map((item) => (
              <VisitCard
                key={item.visitTargetId}
                item={item}
                onItemClick={handleItemClick}
                indicators={indicatorsData?.indicators[item.client.id]}
              />
            ))}
          </>
        )}
      </main>
    </div>
  );
}
